<template>
	<div class="container-fluid mt-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<h2>
				<i class="bi bi-droplet-half me-2"></i>
				Manual Pool Review
			</h2>
		</div>

		<div v-if="loading" class="card">
			<div class="card-body">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>
		</div>

		<div v-else-if="error" class="alert alert-danger">{{ error }}</div>

		<div v-else class="card">
			<div class="table-responsive">
				<table class="table table-hover align-middle mb-0">
					<thead class="table-light">
						<tr>
							<th>Address</th>
							<th>Condition</th>
							<th>Type</th>
							<th>Added by</th>
							<th>Added at</th>
							<th>Exclude from training</th>
							<th class="text-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="task in tasks" :key="task.task_id">
							<td>{{ task.address }}</td>
							<td>
								<span
									class="badge rounded-pill"
									:style="{
										backgroundColor: conditionColorHex(task.condition),
										color: '#fff',
									}"
								>
									{{ task.condition }}
								</span>
							</td>
							<td>{{ poolTypeLabel(task.pool_type) }}</td>
							<td>
								{{ task.created_by?.display_name || `#${task.created_by?.id}` }}
							</td>
							<td>{{ formatDate(task.created_at) }}</td>
							<td>
								<input
									type="checkbox"
									class="form-check-input"
									title="Send exclude_from_training with Confirm"
									:checked="excludeFlags[task.task_id] === true"
									@change="
										excludeFlags[task.task_id] = (
											$event.target as HTMLInputElement
										).checked
									"
								/>
							</td>
							<td class="text-end">
								<ButtonLoading
									class="me-1"
									variant="outline-success"
									size="sm"
									:loading="busy[task.task_id] === true"
									:disabled="busy[task.task_id] === true"
									@click="decide(task, 'confirm')"
								>
									<i class="bi bi-check-lg me-1"></i>Confirm
								</ButtonLoading>
								<button
									class="btn btn-sm btn-outline-danger"
									:disabled="busy[task.task_id]"
									@click="decide(task, 'reject')"
								>
									<i class="bi bi-x-lg me-1"></i>Reject
								</button>
							</td>
						</tr>
						<tr v-if="tasks.length === 0">
							<td colspan="7" class="text-center text-muted py-4">
								No unreviewed manual pool additions.
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
	<ToastNotification
		v-model:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import ToastNotification from "@/components/ToastNotification.vue";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import { poolTypeLabel } from "@/lib/reveal";
import { conditionColorHex } from "@/lib/conditionColors";
import { apiClient } from "@/client";
import type { ManualReviewDecisionRequest, ManualReviewTask } from "@/type/api";

const tasks = ref<ManualReviewTask[]>([]);
const loading = ref(true);
const error = ref("");

// Per-row reviewer decisions: exclude-from-training flag and busy state.
const excludeFlags = reactive<Record<number, boolean>>({});
const busy = reactive<Record<number, boolean>>({});

const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

function formatDate(iso: string): string {
	const d = new Date(iso);
	return isNaN(d.getTime())
		? (iso ?? "").slice(0, 10)
		: d.toLocaleDateString("en-US", {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const data = await apiClient.JSONGet<ManualReviewTask[]>(
			"/api/manual-pool/review",
		);
		tasks.value = data;
		// Seed the per-row checkbox state from the task so unchecking a row
		// that is excluded by default sends an explicit false on confirm.
		for (const t of data) {
			excludeFlags[t.task_id] = !!t.exclude_from_training;
		}
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load review tasks.";
	} finally {
		loading.value = false;
	}
}

async function decide(task: ManualReviewTask, action: "confirm" | "reject") {
	if (busy[task.task_id]) return;
	busy[task.task_id] = true;
	try {
		const body: ManualReviewDecisionRequest = { action };
		if (action === "confirm") {
			body.exclude_from_training = !!excludeFlags[task.task_id];
		}
		await apiClient.JSONPost(`/api/manual-pool/review/${task.task_id}`, body);
		tasks.value = tasks.value.filter((t) => t.task_id !== task.task_id);
		delete excludeFlags[task.task_id];
		showToast(
			action === "confirm" ? "Pool confirmed" : "Pool rejected",
			action === "confirm"
				? "Manual pool addition confirmed."
				: "Manual pool addition rejected and removed.",
		);
	} catch (e: any) {
		showToast(
			"Action failed",
			e?.response?.data?.message ||
				e?.message ||
				"Failed to update the review task.",
		);
	} finally {
		delete busy[task.task_id];
	}
}

onMounted(load);
</script>
